import React from "react";
import { cls } from "@wss/cls";
import { User } from "~/common/Store/models/User";
import Price from "./Price";
import { SelectedProduct } from "~/common/Store/models/ProductListing";
import PlusMemberDiscount from "./PlusMemberDiscount";
import applyBelowMapPricing from "./applyBelowMapPricing";
import applyPlusPricing from "../applyPlusPricing";
import ExclusivePlusPricing from "./Map/ExclusivePlusPricing";
import itemHasMapStyle from "../itemHasMapStyle";
import SaleBadgeAndPricing from "~/common/SaleBadgeAndPricing";
import QuantityDiscountsTable from "./QuantityDiscountsTable";
import getLowestPrice from "../getLowestPrice";
import DivideByPricing from "./DivideByPricing";
interface QuantityDiscountsProps {
  selectedProduct: SelectedProduct;
  user: User;
  productListingView?: string;
  hideQuantityDiscountTable?: boolean;
  displaySalePrice?: boolean;
  setIsQuantityDiscountPopoverOpen?: React.Dispatch<React.SetStateAction<string>>;
}
const QuantityDiscounts = ({
  selectedProduct,
  user,
  productListingView,
  hideQuantityDiscountTable = false,
  displaySalePrice = false,
  setIsQuantityDiscountPopoverOpen
}: QuantityDiscountsProps) => {
  const {
    price,
    unitOfMeasure,
    activeSale,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    divideQuantity,
    divideByUnit
  } = selectedProduct;
  const {
    quantityDiscounts
  } = price;

  // Checks if there is a MAP style available for the product item and if P4(MAP pricing) is lower than the p1 price or sale price(if available).
  const hasMAPStyle = itemHasMapStyle(price, activeSale);
  const hasSalePriceButNoDivideByQuantity = displaySalePrice && !divideQuantity;
  const hasNoSalePriceButHasDivideByQuantity = !displaySalePrice && !!divideQuantity;
  const isUserQualifiedForMapPricing = applyBelowMapPricing({
    price,
    showBelowMinimumAdvertisedPriceForLoggedInCustomers,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers
  }, user);
  const showExclusivePlusPricing = user.hasWebPlus && showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers && hasMAPStyle && !!price.minimumAdvertisedPriceProperties.price;

  // Show Plus Pricing if p5 price is available and it is the lowest price. If there is a map style then first it checks if it's qualifed for below map and if there is a active sale. If not then it returns false. Then it gets the lowest price for map and compares it to plus member discount.
  const showPlusPricing = applyPlusPricing(price, activeSale, hasMAPStyle, !!isUserQualifiedForMapPricing);

  // This function shows the lowest price. It can either show the regular price, sale price or MAP price depending on what the lowest is.
  const lowestPrice = getLowestPrice(price, user, activeSale, showBelowMinimumAdvertisedPriceForLoggedInCustomers, showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers);
  const discountsWithLowerPrice = quantityDiscounts.filter(quantity => quantity.price < lowestPrice);
  const discountsWithLowerPriceAndHigherStartBuy = discountsWithLowerPrice.filter(discount => discount.isLotDiscount || discount.startQuantity >= selectedProduct.startBuy || discount.endQuantity && discount.endQuantity > selectedProduct.startBuy);
  const shouldDisplayQuantityDiscountTable = (hasSalePriceButNoDivideByQuantity || hasNoSalePriceButHasDivideByQuantity || !divideQuantity) && (!hasMAPStyle || isUserQualifiedForMapPricing) && discountsWithLowerPriceAndHigherStartBuy.length > 0;
  const isWishlistPage = window?.location?.pathname.includes("wishlist") ?? false;
  return <div className={`${isWishlistPage ? "flex flex-col justify-center" : ""}`} data-sentry-component="QuantityDiscounts" data-sentry-source-file="QuantityDiscounts.tsx">
      {showPlusPricing ? <div>
          <PlusMemberDiscount productListingView={productListingView} selectedProduct={selectedProduct} user={user} />
          {displaySalePrice && <SaleBadgeAndPricing dataTestId="itemSale" price={price.salePrice} salePricingClassNames={cls("p-0", "my-1", productListingView === "grid" ? "pt-1" : "pt-2 xs:py-1", "text-xs", "text-gray-800", "font-normal")} saleBadgeExtraClassNames={cls("px-1", "mr-1", "py-0.5")} labelClassnames="text-red-500 font-bold" hideRegularLabel />}
        </div> : showExclusivePlusPricing ? <ExclusivePlusPricing mapPrice={price.minimumAdvertisedPriceProperties.price} p1Price={price.price} unitOfMeasure={unitOfMeasure} hasQuantityDiscounts={price.hasQuantityDiscounts && shouldDisplayQuantityDiscountTable} divideByUnit={selectedProduct.divideByUnit} divideQuantity={selectedProduct.divideQuantity} /> : <div>
          <Price price={isUserQualifiedForMapPricing ? lowestPrice : price.price} productListingView={productListingView} unitOfMeasure={unitOfMeasure} displaySalePrice={displaySalePrice} salePrice={price.salePrice} />
          <DivideByPricing dataTestId="divideByPricingQuantityDiscount" price={isUserQualifiedForMapPricing || activeSale ? lowestPrice : price.price} divideByUnit={selectedProduct.divideByUnit} divideQuantity={selectedProduct.divideQuantity} />
        </div>}
      {shouldDisplayQuantityDiscountTable && !hideQuantityDiscountTable ? <QuantityDiscountsTable hasLotDiscounts={selectedProduct.price.hasLotQuantityDiscounts || selectedProduct.price.hasSingleLotQuantityDiscount} quantityDiscounts={discountsWithLowerPriceAndHigherStartBuy} startBuy={selectedProduct.startBuy} productPrice={isUserQualifiedForMapPricing || activeSale ? lowestPrice : price.price} mustBuy={selectedProduct.mustBuy} productListingView={productListingView} selectedProductItemNumber={selectedProduct.itemNumber} unitOfMeasure={unitOfMeasure} divideByUnit={divideByUnit} divideQuantity={divideQuantity} setIsQuantityDiscountPopoverOpen={setIsQuantityDiscountPopoverOpen} productItemLink={selectedProduct.link} /> : null}
      <meta content="USD" data-sentry-element="meta" data-sentry-source-file="QuantityDiscounts.tsx" />
    </div>;
};
export default QuantityDiscounts;